import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import SelectInput from "./select-input";
import { CustomYAxisTick } from "./custom-y-axis-tick";
import { CustomTooltip } from "./custom-tooltip";
import { CustomLegend } from "./custom-legend";

interface BarValue {
  label: string;
  value: number;
  fillColor: string;
}

interface DataPoint {
  category: string;
  gap: number;
  values: BarValue[];
}

interface SelectOption {
  label: string;
  value: string;
}

interface HorizontalBarGraphProps {
  graphTitle: string;
  data: DataPoint[];
  selectedOption?: string;
  placeholder?: string;
  onOptionChange?: (value: string) => void;
  selectOptions?: SelectOption[];
}

const HorizontalBarGraph: React.FC<HorizontalBarGraphProps> = ({
  graphTitle,
  data,
  selectedOption,
  placeholder,
  onOptionChange,
  selectOptions,
}) => {
  // Extract unique bar labels for dynamic Bar rendering
  const barLabels = Array.from(
    new Set(data.flatMap((d) => d.values.map((v) => v.label)))
  );

  return (
    <div className="bg-white p-4 rounded-lg border border-gray-100">
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-medium text-gray-900">{graphTitle}</span>
        {selectedOption && selectOptions && onOptionChange && (
          <SelectInput
            value={selectedOption}
            options={selectOptions}
            placeholder={placeholder}
            onChange={onOptionChange}
          />
        )}
      </div>

      <div className="h-[240px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            layout="vertical"
            data={data}
            margin={{ top: 0, right: 24, bottom: 0, left: 0 }}
            barGap={2}
            barCategoryGap={16}
          >
            <XAxis
              type="number"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: "#64748B" }}
              tickFormatter={(value: number) => value.toString()}
              padding={{ left: 0, right: 8 }}
            />
            <YAxis
              type="category"
              dataKey="category"
              axisLine={false}
              tickLine={false}
              tick={CustomYAxisTick}
              width={180}
            />
            <Tooltip
              content={CustomTooltip}
              cursor={{ fill: "#F8FAFC" }}
              wrapperStyle={{ outline: "none" }}
            />
            <Legend content={CustomLegend} verticalAlign="bottom" height={36} />
            {barLabels.flatMap((label, index) => {
              const barComponents = [
                <Bar
                  key={label}
                  dataKey={`values[${index}].value`}
                  stackId="a"
                  name={label}
                  fill={
                    data[0].values.find((v) => v.label === label)?.fillColor ||
                    "#bce3ff"
                  }
                  barSize={20}
                  radius={[4, 4, 4, 4]}
                  animationDuration={500}
                  animationBegin={index * 100}
                />,
              ];

              if (index < barLabels.length - 1) {
                barComponents.push(
                  <Bar
                    key={`gap_${label}`}
                    dataKey="gap"
                    stackId="a"
                    fill="transparent"
                    barSize={data[0].gap * 100} // Scale gap value to barSize
                    legendType="none"
                    name=" "
                    animationDuration={500}
                    animationBegin={index * 100}
                  />
                );
              }

              return barComponents;
            })}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default HorizontalBarGraph;
